import { Fragment, useState, useEffect } from "react";
import { useLocation, useHistory } from "react-router-dom";
import moment from "moment";
import { postDataWithDotNet } from "../../../newApis/dotNetApiMethods";
import * as ajaxEndpoints from "../../../api/ajax-endpoints";
import { errorHandler } from "../../../helpers/errorHandler";
import Alert from "../../../NewComponents/TypedComponents/Alert/Alert.component";
import BackButtonComponent from "../../../NewComponents/BackButton/BackButton.component";
import { appInsights } from "../../../config/appInsights";
import { IPendingAdminUnlockUser } from "./hooks/useGetPendingAdminUnlockUsers";

const PendingAdminUnlockApproval = () => {
  const [comment, setComment] = useState("");
  const [actionType, setActionType] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const { state } = useLocation<IPendingAdminUnlockUser>();
  const history = useHistory();

  const {
    id,
    userId,
    email,
    firstName,
    lastName,
    phone,
    lockReason,
    unlockReason,
    additionalInfo,
    requestedBy,
    requestedAt,
    status,
  } = state;

  useEffect(() => {
    appInsights.trackPageView({
      name: "PendingAdminUnlockApproval.tsx",
      isLoggedIn: true,
    });
  }, []);

  const handleApproveDecline = async (type: "approve" | "decline") => {
    if (type === "decline" && !comment.trim()) {
      setError("A comment is required when declining an unlock request.");
      return;
    }

    setLoading(true);
    setError(null);

    const reqBody = {
      id,
      userId,
      approved: type === "approve",
      comment: comment.trim() || undefined,
    };

    try {
      await postDataWithDotNet(ajaxEndpoints.APPROVE_ADMIN_UNLOCK, reqBody);
      setSuccess(
        type === "approve"
          ? "Unlock request has been approved successfully."
          : "Unlock request has been declined.",
      );
      setTimeout(() => {
        history.goBack();
      }, 2000);
    } catch (err: any) {
      appInsights.trackException({
        exception: err,
        properties: { fileName: "PendingAdminUnlockApproval.tsx" },
      });
      setError(errorHandler(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Fragment>
      {error && <Alert message={error} />}
      {success && <Alert message={success} type="success" />}

      <div className="d-flex justify-content-between align-items-center flex-wrap mb-3">
        <h3 className="page-title d-flex align-items-center">
          <BackButtonComponent /> Review Unlock Request
        </h3>
        <div className="d-flex align-items-center">
          <button
            className="btn advancly-red-btn btn-md mr-2"
            disabled={loading}
            onClick={() => {
              setActionType("decline");
              handleApproveDecline("decline");
            }}
          >
            {loading && actionType === "decline" ? "Declining..." : "Decline"}
          </button>
          <button
            className="btn advancly-btn btn-md"
            disabled={loading}
            onClick={() => {
              setActionType("approve");
              handleApproveDecline("approve");
            }}
          >
            {loading && actionType === "approve" ? "Approving..." : "Approve"}
          </button>
        </div>
      </div>

      <div className="row mb-4">
        <div className="col-md-4 mb-3">
          <div className="card p-3">
            <small className="text-muted">First Name</small>
            <strong>{firstName}</strong>
          </div>
        </div>
        <div className="col-md-4 mb-3">
          <div className="card p-3">
            <small className="text-muted">Last Name</small>
            <strong>{lastName}</strong>
          </div>
        </div>
        <div className="col-md-4 mb-3">
          <div className="card p-3">
            <small className="text-muted">Email</small>
            <strong>{email}</strong>
          </div>
        </div>
        <div className="col-md-4 mb-3">
          <div className="card p-3">
            <small className="text-muted">Phone Number</small>
            <strong>{phone}</strong>
          </div>
        </div>
        <div className="col-md-4 mb-3">
          <div className="card p-3">
            <small className="text-muted">Lock Reason</small>
            <strong>{lockReason}</strong>
          </div>
        </div>
        <div className="col-md-4 mb-3">
          <div className="card p-3">
            <small className="text-muted">Unlock Reason</small>
            <strong>{unlockReason}</strong>
          </div>
        </div>
        {additionalInfo && (
          <div className="col-md-4 mb-3">
            <div className="card p-3">
              <small className="text-muted">Additional Info</small>
              <strong>{additionalInfo}</strong>
            </div>
          </div>
        )}
        <div className="col-md-4 mb-3">
          <div className="card p-3">
            <small className="text-muted">Requested By</small>
            <strong>{requestedBy}</strong>
          </div>
        </div>
        <div className="col-md-4 mb-3">
          <div className="card p-3">
            <small className="text-muted">Date Requested</small>
            <strong>
              {requestedAt ? moment(requestedAt).format("llll") : ""}
            </strong>
          </div>
        </div>
        <div className="col-md-4 mb-3">
          <div className="card p-3">
            <small className="text-muted">Status</small>
            <strong>{status}</strong>
          </div>
        </div>
      </div>

      <div className="form-group">
        <label htmlFor="unlock-comment">
          Comment <span className="text-muted">(required when declining)</span>
        </label>
        <textarea
          id="unlock-comment"
          className="form-control"
          rows={4}
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder="Enter your comment here..."
        />
      </div>
    </Fragment>
  );
};

export default PendingAdminUnlockApproval;
